import { isEmpty } from "lodash";
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";

import {
  withValidTagTypeConfig,
  transformTagTypeConfigs,
} from "./TagsEditor.util";
import {
  formStateReducer,
  type TagsEditorFormState,
} from "./TagsEditor.formState";
import { useTagsEditorActions } from "./useTagsEditorActions";
import type { TagsEditorProps } from "./tagsEditorTypes";

const useEditorError = () => {
  const [error, setError] = useState<Error | null>(null);
  useEffect(() => {
    if (!error) {
      return;
    }
    const timeoutId = setTimeout(() => setError(null), 5000);
    return () => clearTimeout(timeoutId);
  }, [error]);
  return { error, setError };
};

const useEditorForm = (
  onFinishEdit?: TagsEditorProps["onFinishEdit"],
  onStartEdit?: TagsEditorProps["onStartEdit"]
) => {
  const [
    {
      editMode,
      initialView,
      helpTagType,
      inputTarget,
      attributesModalProps,
      tagValueModalProps,
      newTagCreationModalProps,
    },
    formStateDispatch,
  ] = useReducer(formStateReducer, {
    initialView: true,
    editMode: false,
  } satisfies TagsEditorFormState);

  const startEditing = useCallback(() => {
    formStateDispatch({ type: "startEditing" });
  }, []);
  const finishEditing = useCallback((e?: { target?: unknown }) => {
    formStateDispatch({ type: "finishEditing", inputTarget: e?.target });
  }, []);
  const showTagTypeHelp = useCallback((tagType: unknown) => {
    formStateDispatch({ type: "showTagTypeHelp", tagType });
  }, []);
  const hideHelp = useCallback(() => {
    formStateDispatch({ type: "hideHelp" });
  }, []);

  useEffect(() => {
    const target = inputTarget as { focus?: () => void } | null | undefined;
    target?.focus?.();
  }, [inputTarget]);

  const previousEditModeRef = useRef(editMode);
  useEffect(() => {
    const wasEditing = previousEditModeRef.current;
    previousEditModeRef.current = editMode;
    if (wasEditing && !editMode && !initialView && onFinishEdit) {
      onFinishEdit();
    }
  }, [editMode, initialView, onFinishEdit]);

  useEffect(() => {
    if (editMode && onStartEdit) {
      onStartEdit();
    }
  }, [editMode, onStartEdit]);

  return {
    editMode,
    helpTagType,
    attributesModalProps,
    tagValueModalProps,
    newTagCreationModalProps,
    formStateDispatch,
    startEditing,
    finishEditing,
    showTagTypeHelp,
    hideHelp,
  };
};

type UseTagsEditorParams = Pick<
  TagsEditorProps,
  | "existingTags"
  | "setNewTag"
  | "removeTag"
  | "onFinishEdit"
  | "onStartEdit"
  | "tagTypesConfig"
  | "tagTypesConfigForDisplay"
  | "isAssociationTag"
  | "comments"
  | "entityId"
>;

export const useTagsEditor = ({
  existingTags,
  setNewTag,
  removeTag,
  onFinishEdit,
  onStartEdit,
  tagTypesConfig,
  tagTypesConfigForDisplay,
  comments,
  entityId,
}: UseTagsEditorParams) => {
  const tagSelectorRef = useRef<{
    focus?: () => void;
    blur?: () => void;
  } | null>(null);
  const { error, setError } = useEditorError();
  const form = useEditorForm(onFinishEdit, onStartEdit);
  const displayConfig = tagTypesConfigForDisplay || tagTypesConfig;

  const existingTagsViewModel = useMemo(
    () => withValidTagTypeConfig(existingTags, displayConfig),
    [existingTags, displayConfig]
  );
  const tagTypesConfigViewModel = useMemo(
    () => transformTagTypeConfigs(tagTypesConfig),
    [tagTypesConfig]
  );

  const focusTagSelector = useCallback(() => {
    tagSelectorRef.current?.focus?.();
  }, []);
  const blurTagSelector = useCallback(() => {
    tagSelectorRef.current?.blur?.();
  }, []);

  const actions = useTagsEditorActions({
    form,
    setNewTag,
    removeTag,
    comments,
    entityId,
    tagTypesConfig,
    focusTagSelector,
    blurTagSelector,
  });

  const isTagsEmpty = isEmpty(existingTagsViewModel);
  const noContent = isTagsEmpty && !form.editMode;

  return {
    ...form,
    error,
    setError,
    tagSelectorRef,
    existingTagsViewModel,
    tagTypesConfigViewModel,
    ...actions,
    isTagsEmpty,
    noContent,
    flexStack: noContent ? "0 0 auto" : "1 1 100%",
    addMargin: noContent ? "0" : "s",
  };
};
